// src/utils/myStuffStorage.ts
// Simple UUID generator (no external dependency)
function uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0
      const v = c === 'x' ? r : (r & 0x3 | 0x8)
      return v.toString(16)
    })
}

import { db } from '@/lib/firebase-config'
import { doc, setDoc } from 'firebase/firestore'

export interface SavedReflection {
  id: string
  storyId: string
  storyTitle: string
  reflectionDate: string
  promptType: string
  response: string
  mood: string
  customTitle?: string
  sharedWithParent: boolean
}

class MyStuffStorage {
  private STORAGE_KEY = 'my-reflections'

  // Save a new reflection
  async saveReflection(
    storyId: string,
    storyTitle: string,
    promptType: string,
    response: string,
    mood: string,
    options?: {
      customTitle?: string
      shareWithParent?: boolean
    }
  ): Promise<SavedReflection> {
    const reflection: SavedReflection = {
      id: uuidv4(),
      storyId,
      storyTitle,
      reflectionDate: new Date().toISOString(),
      promptType,
      response,
      mood,
      customTitle: options?.customTitle,
      sharedWithParent: options?.shareWithParent || false
    }

    // Always save to localStorage
    const existing = this.getAllReflections()
    existing.push(reflection)
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(existing))

    // If shared, also save to Firestore
    if (reflection.sharedWithParent) {
      await this.saveToFirestore(reflection)
    }

    return reflection
  }

  // Get all reflections
  getAllReflections(): SavedReflection[] {
    const saved = localStorage.getItem(this.STORAGE_KEY)
    return saved ? JSON.parse(saved) : []
  }

  // Delete a reflection
  deleteReflection(id: string): void {
    const existing = this.getAllReflections()
    const updated = existing.filter(r => r.id !== id)
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updated))
  }

  // Update sharing status
  async updateSharing(id: string, shareWithParent: boolean): Promise<void> {
    const existing = this.getAllReflections()
    const reflection = existing.find(r => r.id === id)
    
    if (reflection) {
      reflection.sharedWithParent = shareWithParent
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(existing))

      if (shareWithParent) {
        await this.saveToFirestore(reflection)
      }
    }
  }

  // Save to Firestore for parent viewing
  private async saveToFirestore(reflection: SavedReflection): Promise<void> {
    const childId = localStorage.getItem('current-child-id')
    if (!childId) return

    try {
      const docRef = doc(db, 'children', childId, 'creative_work', reflection.id)
      await setDoc(docRef, {
        ...reflection,
        createdAt: new Date().toISOString()
      })
      console.log('✅ Reflection shared with parent')
    } catch (error) {
      console.error('❌ Failed to share reflection:', error)
    }
  }
}

export const myStuffStorage = new MyStuffStorage()